import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';
import { TRAINER_ENDPOINTS } from '../auth/authConstants';

export const fetchTrainers = createAsyncThunk(
  'trainers/fetchTrainers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(TRAINER_ENDPOINTS.GET_LIST);
      if (response.data.status) {
        return response.data.data;
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch trainers');
    }
  }
);

export const saveTrainer = createAsyncThunk(
  'trainers/saveTrainer',
  async (trainerData, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      Object.keys(trainerData).forEach(key => {
        if (trainerData[key] !== null && trainerData[key] !== undefined) {
          formData.append(key, trainerData[key]);
        }
      });

      const response = await api.post(TRAINER_ENDPOINTS.SAVE, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.status) {
        return response.data.data;
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to save trainer');
    }
  }
);

export const fetchTrainerById = createAsyncThunk(
  'trainers/fetchTrainerById',
  async (trainerId, { rejectWithValue }) => {
    try {
      const response = await api.get(`${TRAINER_ENDPOINTS.GET_BY_ID}?trainerId=${trainerId}`);
      if (response.data.status) {
        return response.data.data;
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch trainer details');
    }
  }
);

export const deleteTrainer = createAsyncThunk(
  'trainers/deleteTrainer',
  async (trainerId, { rejectWithValue }) => {
    try {
      const response = await api.delete(`${TRAINER_ENDPOINTS.DELETE}?trainerId=${trainerId}`);
      if (response.data.status) {
        return trainerId;
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete trainer');
    }
  }
);

export const fetchTrainerRating = createAsyncThunk(
  'trainers/fetchTrainerRating',
  async (trainerId, { rejectWithValue }) => {
    try {
      const response = await api.get(`${TRAINER_ENDPOINTS.GET_RATING}/${trainerId}`);
      if (response.data.status) {
        return response.data.data;
      }
      return rejectWithValue(response.data.message);
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch trainer rating');
    }
  }
);

const trainerSlice = createSlice({
  name: 'trainers',
  initialState: {
    trainers: [],
    selectedTrainer: null,
    selectedTrainerRating: null,
    loading: false,
    error: null,
    success: false,
  },
  reducers: {
    resetTrainerState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
      state.selectedTrainer = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTrainers.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchTrainers.fulfilled, (state, action) => {
        state.loading = false;
        state.trainers = action.payload;
      })
      .addCase(fetchTrainers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(saveTrainer.pending, (state) => {
        state.loading = true;
      })
      .addCase(saveTrainer.fulfilled, (state) => {
        state.loading = false;
        state.success = true;
      })
      .addCase(saveTrainer.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchTrainerById.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchTrainerById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedTrainer = action.payload;
      })
      .addCase(fetchTrainerById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteTrainer.fulfilled, (state, action) => {
        state.trainers = state.trainers.filter(t => t.trainerId !== action.payload);
      })
      .addCase(fetchTrainerRating.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchTrainerRating.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedTrainerRating = action.payload;
      })
      .addCase(fetchTrainerRating.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { resetTrainerState } = trainerSlice.actions;
export default trainerSlice.reducer;
